import { Fragment, useEffect, useState } from 'react';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import GlobalStyles from '@mui/material/GlobalStyles';
import { styled } from '@mui/material/styles';
import type { EditorState, LexicalEditor } from 'lexical';

import { useSettings, useSharedHistoryContext } from './context';
import {
  AutoFocusPlugin,
  AutoLinkPlugin,
  ClickableLinkPlugin,
  DraggableBlockPlugin,
  FloatingLinkEditorPlugin,
  FloatingTextFormatToolbarPlugin,
  HistoryPlugin,
  LayoutPlugin,
  LexicalErrorBoundary,
  LinkPlugin,
  ListPlugin,
  PasteHandlerPlugin,
  RichTextPlugin,
  TabFocusPlugin,
  TabIndentationPlugin,
  TableActionMenuPlugin,
  TableCellResizerPlugin,
  TableOfContentsPlugin,
  TablePlugin,
  ToolbarPlugin,
} from './plugins';
import { playgroundThemeStyles } from './themes';
import { LexicalContentEditable, LexicalPlaceholder } from './ui';

const StyledRoot = styled('div')({
  width: '100%',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const StyledEditorScroller = styled('div')({
  minHeight: '150px',
  border: 0,
  display: 'flex',
  position: 'relative',
  outline: 0,
  zIndex: 0,
  overflow: 'auto',
  flexGrow: 1,
});

const StyledEditor = styled('div')({
  flex: 'auto',
  position: 'relative',
  zIndex: -1,
});

const StyledEditorContainer = styled('div')(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
  flexGrow: 1,
  minHeight: 0,
  display: 'flex',
  position: 'relative',
}));

type TextEditorProps = {
  placeholder: string;
  onChange: (editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => void;
};

export const TextEditor = (props: TextEditorProps): JSX.Element => {
  const { historyState } = useSharedHistoryContext();
  const {
    settings: { isRichText, showTableOfContents, tableCellMerge },
  } = useSettings();
  const isEditable = useLexicalEditable();
  const placeholder = <LexicalPlaceholder>{props.placeholder}</LexicalPlaceholder>;
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] = useState<boolean>(false);
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport = window.matchMedia('(max-width: 1025px)').matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener('resize', updateViewPortWidth);

    return () => {
      window.removeEventListener('resize', updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  return (
    <Fragment>
      <GlobalStyles
        styles={theme => ({
          ...playgroundThemeStyles(theme),
        })}
      />
      <StyledRoot>
        {isRichText && <ToolbarPlugin setIsLinkEditMode={setIsLinkEditMode} />}
        {showTableOfContents && (
          <Fragment>
            <TableOfContentsPlugin />
            <PasteHandlerPlugin />
          </Fragment>
        )}
        <StyledEditorContainer>
          <OnChangePlugin onChange={props.onChange} />
          <AutoFocusPlugin />
          <AutoLinkPlugin />
          <HistoryPlugin externalHistoryState={historyState} />
          <RichTextPlugin
            contentEditable={
              <StyledEditorScroller>
                <StyledEditor ref={onRef}>
                  <LexicalContentEditable />
                </StyledEditor>
              </StyledEditorScroller>
            }
            placeholder={placeholder}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ListPlugin />
          <TablePlugin hasCellMerge={tableCellMerge} />
          <TableCellResizerPlugin />
          <LinkPlugin />
          <ClickableLinkPlugin disabled={isEditable} />
          <TabFocusPlugin />
          <TabIndentationPlugin />
          <LayoutPlugin />
          {floatingAnchorElem && !isSmallWidthViewport && (
            <Fragment>
              <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
              <FloatingLinkEditorPlugin
                anchorElem={floatingAnchorElem}
                isLinkEditMode={isLinkEditMode}
                setIsLinkEditMode={setIsLinkEditMode}
              />
              <TableActionMenuPlugin anchorElem={floatingAnchorElem} isCellMergeEnabled />
              <FloatingTextFormatToolbarPlugin anchorElem={floatingAnchorElem} setIsLinkEditMode={setIsLinkEditMode} />
            </Fragment>
          )}
        </StyledEditorContainer>
      </StyledRoot>
    </Fragment>
  );
};
